import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { fetchResorts } from "@/lib/resortsCache";

const FAVORITES_KEY = "tg_favorites_v1";
const COMPARE_KEY = "tg_compare_resorts_v1";

const readStored = (key, fallback) => {
  try {
    const value = JSON.parse(localStorage.getItem(key) || "null");
    return Array.isArray(value) ? value : fallback;
  } catch {
    return fallback;
  }
};

const favoriteId = (item) => `${item.type}:${item.countrySlug || ""}:${item.slug}`;

export const resortSnapshot = (r) => ({
  type: "resort",
  slug: r.slug,
  title: r.name,
  href: `/maldives/${r.slug}`,
  image: r.images?.[0]?.file || "",
  category: r.category,
  location: r.location || "",
  transfer: r.transfer || "",
  transferTime: r.transferTime || "",
  filters: r.filters || {},
  mealPlans: r.mealPlans || [],
  kids: r.kids || "",
  adultsOnly: r.adultsOnly,
});

export const programSnapshot = (p) => ({
  type: "program",
  slug: p.slug,
  countrySlug: p.countrySlug,
  title: p.title,
  href: `/programs/${p.countrySlug}/${p.slug}`,
  image: p.heroImage?.file || "",
  nightsTotal: p.nightsTotal,
  cities: p.cities || [],
});

const FavoritesContext = createContext(null);

export const FavoritesProvider = ({ children }) => {
  const [favorites, setFavorites] = useState(() => readStored(FAVORITES_KEY, []));
  const [compare, setCompare] = useState(() => readStored(COMPARE_KEY, []));

  useEffect(() => {
    localStorage.setItem(FAVORITES_KEY, JSON.stringify(favorites));
  }, [favorites]);

  useEffect(() => {
    localStorage.setItem(COMPARE_KEY, JSON.stringify(compare));
  }, [compare]);

  // لا تجلب شيئًا لزائر بلا محفوظات — التنظيف لا معنى له والطلب يصبح غير ضروري
  // (تحديدًا في صفحات المخططات والعروض). من لديه مفضلة تُجلب بياناتها وتُحدَّث كالمعتاد.
  const hasStored = favorites.length > 0 || compare.length > 0;
  useEffect(() => {
    if (!hasStored) return undefined;
    let cancelled = false;
    Promise.all([fetchResorts(), api.get("/programs")])
      .then(([resorts, programsResponse]) => {
        if (cancelled) return;
        const visibleIds = new Set();
        resorts.forEach((r) => visibleIds.add(`resort::${r.slug}`));
        (programsResponse.data || []).forEach((p) => visibleIds.add(`program:${p.countrySlug}:${p.slug}`));
        const visibleResortSlugs = new Set(resorts.map((r) => r.slug));
        setFavorites((items) => items.filter((item) => visibleIds.has(item.id)));
        setCompare((items) => items.filter((item) => visibleResortSlugs.has(item.slug)));
      })
      .catch(() => {});
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value = useMemo(() => {
    const isFavorite = (item) => favorites.some((f) => f.id === favoriteId(item));
    const toggleFavorite = (item) => {
      const normalized = { ...item, id: favoriteId(item), savedAt: new Date().toISOString() };
      const exists = isFavorite(normalized);
      setFavorites((items) => exists ? items.filter((f) => f.id !== normalized.id) : [normalized, ...items]);
      toast.success(exists ? "أُزيل من المفضلة" : "حُفظ في المفضلة على هذا الجهاز");
      return !exists;
    };
    const removeFavorite = (id) => setFavorites((items) => items.filter((f) => f.id !== id));
    const isComparing = (slug) => compare.some((r) => r.slug === slug);
    const toggleCompare = (resort) => {
      const snapshot = resortSnapshot(resort);
      if (isComparing(snapshot.slug)) {
        setCompare((items) => items.filter((r) => r.slug !== snapshot.slug));
        return true;
      }
      if (compare.length >= 3) {
        toast.error("يمكن مقارنة 3 منتجعات كحد أقصى");
        return false;
      }
      setCompare((items) => [...items, snapshot]);
      toast.success("أُضيف إلى المقارنة");
      return true;
    };
    const clearCompare = () => setCompare([]);
    return { favorites, toggleFavorite, removeFavorite, isFavorite, compare, toggleCompare, isComparing, clearCompare };
  }, [favorites, compare]);

  return <FavoritesContext.Provider value={value}>{children}</FavoritesContext.Provider>;
};

export const useFavorites = () => useContext(FavoritesContext);
